import {
    AreasLayer,
    CorvusMap,
    DocksLayer,
    DronesLayer,
    FlylinesLayer,
    FlyzonesLayer,
    MarkersLayer,
    MeasurementsLayer,
    PointsLayer,
    WarningsLayer,
} from "@corvus/corvus-map";
import {
    combineWorldExtents,
    parseAreas,
    parseDocks,
    parseDrones,
    parseFlyzones,
    parseMarkers,
    parseMeasurements,
    parseWarnings,
    resolveMeasurementPose,
} from "@corvus/corvus-map/model";
import { where } from "firebase/firestore";
import React, {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    Alert,
    Button,
    OverlayTrigger,
    Spinner,
    Tooltip,
} from "react-bootstrap";
import {
    FaCircle,
    FaClock,
    FaExclamationCircle,
    FaImages,
    FaRedo,
} from "react-icons/fa";
import { FaMagnifyingGlass } from "react-icons/fa6";
import { MdPower, MdPowerOff } from "react-icons/md";
import { TbMapPinExclamation, TbMapPinPlus } from "react-icons/tb";

import * as api from "../../api/api";
import {
    useAreasDocument,
    useDockCollection,
    useFlightDocument,
    useFlyzonesDocument,
    useMarkersDocument,
    useMeasurementCollection,
} from "../../api/firestoreWrappers.generated.jsx";
import { useUser } from "../../contexts/user_provider";
import { getFirebaseStorageDownloadUrl } from "../../corvusFirebase";
import { useDronesOnline } from "../../hooks/useDroneOnline.jsx";
import useSupportsHover from "../../hooks/useSupportsHover.jsx";
import urls from "../../urls.jsx";
import MeasurementImageOverlays from "./MeasurementImageOverlays";
import { useProjectedMeasurementImages } from "./useProjectedMeasurementImages";
import {
    generateCommandFlylineObjects,
    generateReturnFlylineObjects,
    replaceCommandsSettingsWithArguments,
} from "./utils/FlightPathUtils";

const PREVIEW_MARGIN = 12;
const PREVIEW_OFFSET = 30;
const PREVIEW_MAX_WIDTH = 512;
const PREVIEW_MAX_VIEWPORT_WIDTH_RATIO = 0.8;
const PREVIEW_MAX_VIEWPORT_HEIGHT_RATIO = 0.4;

function getMeasurementPreviewSize({
    naturalWidth,
    naturalHeight,
    containerWidth,
    containerHeight,
}) {
    const maxWidth = Math.min(
        PREVIEW_MAX_WIDTH,
        containerWidth * PREVIEW_MAX_VIEWPORT_WIDTH_RATIO,
    );
    const maxHeight = containerHeight * PREVIEW_MAX_VIEWPORT_HEIGHT_RATIO;

    const scale = Math.min(
        maxWidth / naturalWidth,
        maxHeight / naturalHeight,
        1,
    );

    return {
        width: naturalWidth * scale,
        height: naturalHeight * scale,
    };
}

function clampPreviewX(x, previewWidth, containerWidth) {
    const halfWidth = previewWidth / 2;
    const minX = PREVIEW_MARGIN + halfWidth;
    const maxX = containerWidth - PREVIEW_MARGIN - halfWidth;

    if (maxX < minX) {
        return containerWidth / 2;
    }

    return Math.min(Math.max(x, minX), maxX);
}

function normalizeHighlightStyle(style) {
    if (!style) return undefined;

    const color =
        typeof style.color === "string"
            ? Number.parseInt(style.color.replace(/^#/, ""), 16)
            : style.color;

    return {
        ...style,
        color,
    };
}

function createBatchAreaObject(batch) {
    if (!batch.rowKey) {
        throw new Error(
            "Batch rowKey is required for map presentation identity.",
        );
    }

    const x = Number(batch.area.min.x);
    const y = Number(batch.area.min.y);
    const maxX = Number(batch.area.max.x);
    const maxY = Number(batch.area.max.y);

    return {
        id: `batch:${batch.rowKey}`,
        type: "area",
        label: String(batch.data?.name ?? ""),
        x,
        y,
        z: 0,
        width: maxX - x,
        height: maxY - y,
    };
}

function matchesObjectRef(info, objectRef) {
    return (
        info?.objectRef.layerId === objectRef.layerId &&
        info.objectRef.id === objectRef.id
    );
}

function getMeasurementsArea(measurements, poseMode) {
    if (!measurements || measurements.length === 0) {
        return null; // Return null if the list is empty
    }

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    let hasResolvedMeasurement = false;

    for (const measurement of measurements) {
        const resolvedPose = resolveMeasurementPose(measurement, poseMode);
        if (!resolvedPose) continue;

        const { x, y } = resolvedPose.pose.position;

        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
        hasResolvedMeasurement = true;
    }

    if (!hasResolvedMeasurement) return null;

    return {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
    };
}

function getCommandsArea(commands) {
    if (!commands || commands.length === 0) {
        return null; // Return null if the list is empty
    }

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    for (const command of commands) {
        if (
            command.type === "SCHEDULE_MOVE_XYZ" ||
            command.type === "SCHEDULE_FLY_TO_XY" ||
            command.type === "SCHEDULE_TAKEOFF"
        ) {
            const { x, y } = command.arguments;

            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
        }
    }

    return {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
    };
}

export default function MapView({
    organizationId,
    locationId,
    flightId,
    missionId,
    taskPreview, // taskId + droneId
    resumePreview, // missionId + droneId
    point, // x + y
    lockFocusOnPoint = true,
    highlightedDockId,
    inspect,
    onInspectChange,
    onMapClick,
    setTaskPreviewSuccess,
    areasToShow,
    areaColors,
    batches,
}) {
    const mapRef = useRef(null);
    const viewportRef = useRef(null);
    const measurementModeTooltipId = useId();
    const inspectModeTooltipId = useId();
    const measurementPoseModeTooltipId = useId();
    const supportsHover = useSupportsHover();
    const [frameDimensions, setFrameDimensions] = useState(null);
    const { getUserIsAdmin } = useUser();
    const isAdmin = useMemo(() => getUserIsAdmin(), [getUserIsAdmin]);
    const [disableAutoFocus, setDisableAutoFocus] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hoverTextInfo, setHoverTextInfo] = useState(null);
    const [hoverImageSize, setHoverImageSize] = useState(null);
    const [hoverImageInfo, setHoverImageInfo] = useState(null);
    const [hoverImageError, setHoverImageError] = useState(false);
    const [measurementPresentation, setMeasurementPresentation] =
        useState("points");
    const [measurementPoseMode, setMeasurementPoseMode] = useState(
        "telemetry-optimized",
    );
    const measurementPoseContextKey = useMemo(
        () => [organizationId, locationId, missionId].join("\u0000"),
        [locationId, missionId, organizationId],
    );
    const measurementPoseContextRef = useRef(measurementPoseContextKey);
    const mosaicAvailable = missionId !== undefined && getUserIsAdmin();
    const showMosaic = mosaicAvailable && measurementPresentation === "mosaic";
    const [selectedMosaicMeasurementId, setSelectedMosaicMeasurementId] =
        useState(null);
    const [mapReadiness, setMapReadiness] = useState({
        worldRectKey: null,
        generation: 0,
    });
    const measurementImageUrlsRef = useRef(new Map());
    const measurementImagePromisesRef = useRef(new Map());

    // These components are always loaded
    const [areasDoc] = useAreasDocument(organizationId, locationId);
    const areas = useMemo(
        () => (areasToShow ?? areasDoc?.areas) || {},
        [areasToShow, areasDoc?.areas],
    );
    const areaObjects = useMemo(() => parseAreas(areas), [areas]);
    const renderedAreaObjects = useMemo(
        () => areaObjects.map((area) => ({ ...area, interactive: false })),
        [areaObjects],
    );
    const areaHighlightStyles = useMemo(() => {
        const highlights = new Map();

        Object.entries(areas).forEach(([colName, rows]) => {
            Object.keys(rows).forEach((rowName) => {
                const style = normalizeHighlightStyle(
                    areaColors?.[colName]?.[rowName],
                );
                if (style) highlights.set(`area:${colName}:${rowName}`, style);
            });
        });

        return highlights;
    }, [areas, areaColors]);
    const batchAreaObjects = useMemo(
        () => (batches ?? []).map(createBatchAreaObject),
        [batches],
    );
    const renderedBatchAreaObjects = useMemo(
        () =>
            batchAreaObjects.map((batchArea) => ({
                ...batchArea,
                interactive: false,
            })),
        [batchAreaObjects],
    );
    const batchHighlightStyles = useMemo(() => {
        const highlights = new Map();

        batchAreaObjects.forEach((batchArea, index) => {
            if (batches?.[index]?.highlighted) {
                highlights.set(batchArea.id, { color: 0x4287f5, alpha: 0.5 });
            }
        });

        return highlights;
    }, [batchAreaObjects, batches]);
    const [flyzonesDoc] = useFlyzonesDocument(organizationId, locationId);
    const flyzoneObjects = useMemo(
        () => parseFlyzones(flyzonesDoc?.flyzones),
        [flyzonesDoc?.flyzones],
    );
    const renderedFlyzoneObjects = useMemo(
        () =>
            flyzoneObjects.map((flyzone) => ({
                ...flyzone,
                interactive: Boolean(inspect),
            })),
        [flyzoneObjects, inspect],
    );
    const [markersDoc] = useMarkersDocument(organizationId, locationId);
    const markerObjects = useMemo(
        () => parseMarkers(markersDoc?.markers),
        [markersDoc?.markers],
    );
    const renderedMarkerObjects = useMemo(
        () =>
            markerObjects.map((marker) => ({
                ...marker,
                interactive: Boolean(inspect),
            })),
        [markerObjects, inspect],
    );
    const [docks] = useDockCollection(organizationId, locationId);
    const dockObjects = useMemo(() => parseDocks(docks), [docks]);
    const pointObjects = useMemo(() => {
        const x = Number(point?.x);
        const y = Number(point?.y);
        if (!Number.isFinite(x) || !Number.isFinite(y)) return [];

        return [
            {
                id: "requested-point",
                type: "point",
                x,
                y,
                color: 0x00ffff,
                strokeColor: 0x000000,
                interactive: false,
            },
        ];
    }, [point]);
    const [droneConfigs] = api.useDroneConfigsFromLocation(
        organizationId,
        locationId,
    );
    const [droneStates] = api.useDroneStatesFromLocation(
        organizationId,
        locationId,
        { type: "map" },
    );
    const droneIds = useMemo(
        () => droneConfigs.map((droneConfig) => droneConfig.id),
        [droneConfigs],
    );
    const onlineStatuses = useDronesOnline(droneIds);

    // These components are loaded based on inputs
    const [flightDoc] = useFlightDocument(organizationId, locationId, flightId);
    const flight = useMemo(() => {
        if (!flightDoc) return flightDoc;

        const { commands, ...rest } = flightDoc;
        if (commands) {
            const updatedCommands =
                replaceCommandsSettingsWithArguments(commands);
            return { ...rest, commands: updatedCommands };
        }

        return flightDoc;
    }, [flightDoc]);
    const [measurements] = useMeasurementCollection(
        organizationId,
        locationId,
        [where("meta.mission.id", "==", missionId)],
    );
    const measurementPoseAvailability = useMemo(() => {
        if (!Array.isArray(measurements)) return null;

        let hasTelemetryOptimizedPose = false;
        let hasOriginalPose = false;

        for (const measurement of measurements) {
            const resolvedPose = resolveMeasurementPose(
                measurement,
                "telemetry-optimized",
            );
            if (!resolvedPose) continue;

            if (resolvedPose.source === "telemetry-optimized") {
                hasTelemetryOptimizedPose = true;
            } else {
                hasOriginalPose = true;
            }

            if (hasTelemetryOptimizedPose && hasOriginalPose) return "mixed";
        }

        return hasTelemetryOptimizedPose ? "telemetry-optimized" : null;
    }, [measurements]);
    const selectedMeasurementPoseMode =
        measurementPoseContextRef.current === measurementPoseContextKey
            ? measurementPoseMode
            : "telemetry-optimized";
    const activeMeasurementPoseMode = measurementPoseAvailability
        ? selectedMeasurementPoseMode
        : "original";
    useEffect(() => {
        if (measurementPoseContextRef.current === measurementPoseContextKey) {
            return;
        }

        measurementPoseContextRef.current = measurementPoseContextKey;
        setMeasurementPoseMode("telemetry-optimized");
    }, [measurementPoseContextKey]);
    const measurementObjects = useMemo(
        () => parseMeasurements(measurements, activeMeasurementPoseMode),
        [activeMeasurementPoseMode, measurements],
    );
    const projectedMeasurementImages = useProjectedMeasurementImages({
        enabled: showMosaic,
        organizationId,
        locationId,
        measurements,
        areas: areaObjects,
        poseMode: activeMeasurementPoseMode,
    });
    const [taskPreviewCommands, setTaskPreviewCommands] = useState([]);
    const [resumePreviewCommands, setResumePreviewCommands] = useState([]);
    const [previewAlert, setPreviewAlert] = useState(null);
    const [hasSentWakeUp, setHasSentWakeUp] = useState(false);

    // Memoized data
    const routePlannerVersions = useMemo(() => {
        const versionsMap = {};
        droneConfigs.forEach((config) => {
            versionsMap[config.id] = config.routeplanner?.version;
        });
        return versionsMap;
    }, [droneConfigs]);

    const droneModels = useMemo(() => {
        const modelMap = {};
        droneConfigs.forEach((config) => {
            modelMap[config.id] = config.model;
        });
        return modelMap;
    }, [droneConfigs]);

    const flightCommands = useMemo(
        () => flight?.commands || [],
        [flight?.commands],
    );
    const fligthAbortEvents = useMemo(() => {
        return (
            flight?.events.filter((event) => event.type === "FLIGHT_ABORTED") ||
            []
        );
    }, [flight?.events]);

    const warningObjects = useMemo(() => {
        const emergencyLandings =
            flight?.events
                .map((event, index) => ({ event, index }))
                .filter(
                    ({ event }) =>
                        event.type === "UNCONTROLLED_EMERGENCY_LANDING",
                ) || [];

        return parseWarnings(
            emergencyLandings.map(({ event, index }) => ({
                id: `uncontrolled-emergency-landing:${event.timestamp.seconds}:${event.timestamp.nanoseconds}:${index}`,
                x: event.drone.position.x,
                y: event.drone.position.y,
                label: "Uncontrolled emergency landing",
            })),
        );
    }, [flight?.events]);

    const operationalDrones = useMemo(() => {
        return Object.keys(droneStates)
            .map((droneId) => {
                const droneData = droneStates[droneId];
                if (!droneData) return null;
                if (!droneData.widgets.position) return null;
                if (!droneData.widgets.orientation) return null;

                return {
                    droneId,
                    x: droneData.widgets.position.data.x,
                    y: droneData.widgets.position.data.y,
                    yaw: droneData.widgets.orientation.data.yaw,
                    isSleeping: droneData?.widgets?.status?.data === "Sleeping",
                    isOnline: onlineStatuses[droneId],
                    bestReturnCommand:
                        droneData.widgets?.bestReturnCommand?.data,
                    detectedMarkers: droneData.widgets?.detectedMarkers?.data,
                    batteryPercentage: droneData.widgets?.battery?.data,
                    isCharging: droneData.widgets?.isCharging?.data,
                    model: droneModels?.[droneId],
                };
            })
            .filter(Boolean); // To filter out the nulls
    }, [droneStates, onlineStatuses, droneModels]);

    const droneObjects = useMemo(
        () =>
            parseDrones(
                operationalDrones.map(
                    ({
                        droneId,
                        x,
                        y,
                        yaw,
                        isOnline,
                        batteryPercentage,
                        isCharging,
                        model,
                    }) => ({
                        id: droneId,
                        x,
                        y,
                        yaw,
                        isOnline,
                        batteryPercentage,
                        isCharging,
                        model,
                    }),
                ),
            ),
        [operationalDrones],
    );
    const renderedDroneObjects = useMemo(
        () => droneObjects.map((drone) => ({ ...drone, interactive: false })),
        [droneObjects],
    );

    const detectedMarkerIds = useMemo(() => {
        const allIds = operationalDrones.flatMap(
            (drone) => drone.detectedMarkers ?? [],
        );
        return new Set(allIds.map(String));
    }, [operationalDrones]);

    const taskPreviewFlylineObjects = useMemo(
        () =>
            generateCommandFlylineObjects(taskPreviewCommands, {
                idPrefix: "task-preview",
            }),
        [taskPreviewCommands],
    );
    const resumePreviewFlylineObjects = useMemo(
        () =>
            generateCommandFlylineObjects(resumePreviewCommands, {
                idPrefix: "resume-preview",
            }),
        [resumePreviewCommands],
    );
    const flightPathFlylineObjects = useMemo(
        () =>
            generateCommandFlylineObjects(flightCommands, {
                abortEvents: fligthAbortEvents,
                idPrefix: "flight-path",
            }),
        [flightCommands, fligthAbortEvents],
    );
    const returnPathFlylineObjects = useMemo(
        () =>
            generateReturnFlylineObjects(
                operationalDrones.find(
                    (drone) => drone?.droneId === flight?.drone?.id,
                ),
                flightCommands,
                "return-path",
            ),
        [operationalDrones, flightCommands, flight?.drone?.id],
    );

    // Handle resizing
    useEffect(() => {
        if (!mapRef.current) return;

        const observer = new ResizeObserver(() => {
            const { width, height } = mapRef.current.getBoundingClientRect();
            setFrameDimensions({ width, height });
        });

        observer.observe(mapRef.current);

        return () => observer.disconnect();
    }, []);

    const worldRect = useMemo(
        () =>
            combineWorldExtents([
                ...areaObjects,
                ...markerObjects.map(({ x, y }) => ({ x, y })),
                ...flyzoneObjects,
                ...batchAreaObjects,
            ]),
        [areaObjects, markerObjects, flyzoneObjects, batchAreaObjects],
    );
    const worldRectKey = worldRect
        ? JSON.stringify([
              worldRect.x,
              worldRect.y,
              worldRect.width,
              worldRect.height,
          ])
        : null;
    const mapReady =
        worldRectKey !== null && mapReadiness.worldRectKey === worldRectKey;

    const handleMapReady = useCallback(() => {
        setMapReadiness((current) => ({
            worldRectKey,
            generation: current.generation + 1,
        }));
    }, [worldRectKey]);

    const selectedDockIds = useMemo(
        () =>
            highlightedDockId === undefined || highlightedDockId === null
                ? new Set()
                : new Set([String(highlightedDockId)]),
        [highlightedDockId],
    );
    const selectedMosaicMeasurementIds = useMemo(
        () =>
            selectedMosaicMeasurementId === null
                ? new Set()
                : new Set([selectedMosaicMeasurementId]),
        [selectedMosaicMeasurementId],
    );

    useEffect(() => {
        if (
            selectedMosaicMeasurementId !== null &&
            (!showMosaic ||
                !projectedMeasurementImages.some(
                    ({ id }) => id === selectedMosaicMeasurementId,
                ))
        ) {
            setSelectedMosaicMeasurementId(null);
        }
    }, [projectedMeasurementImages, selectedMosaicMeasurementId, showMosaic]);

    const showTextTooltip = useCallback((event, text) => {
        setHoverTextInfo({
            objectRef: event.objectRef,
            x: event.screenPosition.x,
            y: event.screenPosition.y,
            text,
        });
    }, []);

    const clearTextTooltip = useCallback((event) => {
        setHoverTextInfo((current) =>
            matchesObjectRef(current, event.objectRef) ? null : current,
        );
    }, []);

    const handleFlyzoneHover = useCallback(
        (event) => {
            const { name, zMin, zMax } = event.item;
            showTextTooltip(event, `${name},\n Z: [${zMin}, ${zMax}]`);
        },
        [showTextTooltip],
    );

    const handleMarkerHover = useCallback(
        (event) => {
            const { id, x, y, z, height } = event.item;
            showTextTooltip(
                event,
                `id: ${id} \n X:${x}, Y:${y}, Z:${z} \n Height: ${height}`,
            );
        },
        [showTextTooltip],
    );

    const handleDockHover = useCallback(
        (event) => {
            const { powered, label, z } = event.item;
            showTextTooltip(
                event,
                <>
                    {powered === true && (
                        <MdPower className="text-success mb-1" />
                    )}
                    {powered === false && (
                        <MdPowerOff className="text-secondary mb-1" />
                    )}
                    {`${label}`}
                    <br />
                    {`Z: ${z}`}
                </>,
            );
        },
        [showTextTooltip],
    );

    const handleFlylineHover = useCallback(
        (event) => {
            if (event.item.z !== undefined) {
                showTextTooltip(event, `z: ${event.item.z.toFixed(2)}`);
            }
        },
        [showTextTooltip],
    );

    const handleWarningHover = useCallback(
        (event) => {
            showTextTooltip(event, event.item.label);
        },
        [showTextTooltip],
    );

    const handleMeasurementHover = useCallback(
        (event) => {
            const { id, yaw, deleted } = event.item;
            const imageLocation = `organizations/${organizationId}/locations/${locationId}/measurements/512x512/${id}_512x512.jpg`;
            const cachedImageUrl = deleted
                ? "Deleted"
                : (measurementImageUrlsRef.current.get(imageLocation) ?? null);

            setHoverImageInfo({
                objectRef: event.objectRef,
                x: event.screenPosition.x,
                y: event.screenPosition.y,
                rotation:
                    yaw !== undefined
                        ? -Math.PI / 2 - yaw * (Math.PI / 180)
                        : 0,
                imageUrl: cachedImageUrl,
            });

            if (
                deleted ||
                cachedImageUrl ||
                measurementImagePromisesRef.current.has(imageLocation)
            )
                return;

            const imagePromise = getFirebaseStorageDownloadUrl(imageLocation)
                .catch(() => "Unavailable")
                .then((imageUrl) => {
                    measurementImageUrlsRef.current.set(
                        imageLocation,
                        imageUrl,
                    );
                    setHoverImageInfo((current) =>
                        matchesObjectRef(current, event.objectRef)
                            ? { ...current, imageUrl }
                            : current,
                    );
                })
                .finally(() => {
                    measurementImagePromisesRef.current.delete(imageLocation);
                });

            measurementImagePromisesRef.current.set(
                imageLocation,
                imagePromise,
            );
        },
        [organizationId, locationId],
    );

    const clearMeasurementPreview = useCallback((event) => {
        setHoverImageInfo((current) =>
            matchesObjectRef(current, event.objectRef) ? null : current,
        );
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Reset the measured preview size when the hovered object identity changes.
    useEffect(() => {
        setHoverImageSize(null);
    }, [hoverImageInfo?.objectRef?.layerId, hoverImageInfo?.objectRef?.id]);

    const handleMeasurementClick = useCallback(
        (event) => {
            window.open(
                urls.measurement(
                    organizationId,
                    locationId,
                    missionId,
                    event.item.id,
                ),
                "_blank",
            );
        },
        [organizationId, locationId, missionId],
    );

    const handleMosaicMeasurementClick = useCallback((event) => {
        setSelectedMosaicMeasurementId((current) =>
            current === null ? event.item.id : null,
        );
    }, []);

    const handleUnhandledClick = useCallback(
        (event) => {
            onMapClick?.(event.worldPosition);

            if (showMosaic) {
                setSelectedMosaicMeasurementId(null);
            }
        },
        [onMapClick, showMosaic],
    );

    const toggleMeasurementPresentation = useCallback(() => {
        setMeasurementPresentation((current) =>
            current === "points" ? "mosaic" : "points",
        );
        setSelectedMosaicMeasurementId(null);
        setHoverImageInfo(null);
        setHoverImageSize(null);
    }, []);

    const measurementPresentationAction = showMosaic
        ? "Show measurement points"
        : "Show mosaic";
    const measurementModeButton = (
        <Button
            variant="light"
            size="sm"
            aria-label={measurementPresentationAction}
            onClick={toggleMeasurementPresentation}
            style={{
                width: "40px",
                height: "40px",
                padding: 0,
                border: "1px solid rgba(0, 0, 0, 0.2)",
                boxShadow: "0 1px 4px rgba(0, 0, 0, 0.25)",
            }}
        >
            {showMosaic ? (
                <FaCircle size="10px" aria-hidden="true" color="rgb(255,0,0)" />
            ) : (
                <FaImages aria-hidden="true" />
            )}
        </Button>
    );
    const inspectModeAction = inspect
        ? "Disable inspect mode"
        : "Enable inspect mode";
    const inspectModeButton = isAdmin && onInspectChange && (
        <Button
            variant={inspect ? "primary" : "light"}
            active={inspect}
            aria-pressed={inspect}
            aria-label={inspectModeAction}
            onClick={() => onInspectChange(!inspect)}
            style={{
                width: "40px",
                height: "40px",
                padding: 0,
                border: "1px solid rgba(0, 0, 0, 0.2)",
                boxShadow: inspect
                    ? "inset 0 1px 3px rgba(0, 0, 0, 0.3)"
                    : "0 1px 4px rgba(0, 0, 0, 0.25)",
            }}
        >
            <FaMagnifyingGlass aria-hidden="true" />
        </Button>
    );
    const measurementPoseModeSelected =
        activeMeasurementPoseMode === "telemetry-optimized";
    const measurementPoseModeAction = measurementPoseModeSelected
        ? "Use original pose"
        : "Use optimized pose";
    const measurementPoseModeButton = isAdmin &&
        measurementPoseAvailability && (
            <Button
                variant={measurementPoseModeSelected ? "primary" : "light"}
                active={measurementPoseModeSelected}
                aria-pressed={measurementPoseModeSelected}
                aria-label={measurementPoseModeAction}
                onClick={() =>
                    setMeasurementPoseMode((currentMode) =>
                        currentMode === "telemetry-optimized"
                            ? "original"
                            : "telemetry-optimized",
                    )
                }
                style={{
                    width: "40px",
                    height: "40px",
                    padding: 0,
                    border: "1px solid rgba(0, 0, 0, 0.2)",
                    boxShadow: measurementPoseModeSelected
                        ? "inset 0 1px 3px rgba(0, 0, 0, 0.3)"
                        : "0 1px 4px rgba(0, 0, 0, 0.25)",
                }}
            >
                {measurementPoseAvailability === "telemetry-optimized" ? (
                    <TbMapPinPlus aria-hidden="true" />
                ) : (
                    <TbMapPinExclamation aria-hidden="true" />
                )}
            </Button>
        );

    const taskPreviewDroneId =
        taskPreview?.droneId &&
        taskPreview.droneId !== "none" &&
        taskPreview.droneId !== "ANY"
            ? taskPreview.droneId
            : undefined;
    const taskPreviewRoutePlannerVersion = taskPreviewDroneId
        ? routePlannerVersions[taskPreviewDroneId]
        : "3.4.0"; // TODO change to stable after 3.4.0 is stable
    const taskPreviewUsesStartPosition = Boolean(taskPreview?.useStartPosition);
    const taskPreviewStartPosition = taskPreview?.startPosition;

    const resumePreviewRoutePlannerVersion =
        resumePreview?.droneId &&
        Object.hasOwn(routePlannerVersions, resumePreview.droneId)
            ? routePlannerVersions[resumePreview.droneId]
            : undefined;

    const loadTaskPreview = useCallback(() => {
        if (
            (!taskPreviewDroneId && !taskPreviewStartPosition) ||
            (taskPreviewUsesStartPosition && !taskPreviewStartPosition)
        ) {
            setTaskPreviewCommands([]);
            setPreviewAlert(null);
            setTaskPreviewSuccess?.(false);
            return;
        }

        if (
            organizationId &&
            locationId &&
            taskPreview?.taskId &&
            (taskPreviewDroneId || taskPreviewStartPosition) &&
            taskPreviewRoutePlannerVersion
        ) {
            if (taskPreview.taskId === "none") {
                setTaskPreviewCommands([]);
                setPreviewAlert(null);
                setTaskPreviewSuccess?.(false);
                return;
            }

            setIsLoading(true);

            api.getFlightPreview(
                organizationId,
                locationId,
                taskPreviewDroneId,
                taskPreview.taskId,
                taskPreviewRoutePlannerVersion,
                taskPreviewStartPosition,
            )
                .then((response) => {
                    if (taskPreviewRoutePlannerVersion === "V2") {
                        response.data = response.data.data;
                    }

                    // It could be that response.data.data is a string "mission completed". We ignore that for now. Could be handled later
                    if (Array.isArray(response.data)) {
                        const transformedCommands =
                            replaceCommandsSettingsWithArguments(response.data);
                        setTaskPreviewCommands(transformedCommands);
                        setTaskPreviewSuccess?.(true);
                    }

                    setPreviewAlert(null);
                })
                .catch((error) => {
                    setTaskPreviewCommands([]);
                    let userErrorMessage = "";
                    if (error?.response?.status) {
                        switch (error.response.status) {
                            case 520:
                                userErrorMessage =
                                    "There is no app in the requested task. Add an app to the task.";
                                break;
                            case 521:
                                userErrorMessage =
                                    "Drone is not in a location where it is allowed to fly. Place the drone in a flyzone.";
                                break;
                            case 522:
                                userErrorMessage =
                                    "Drone has not enough space to take off. Place the drone somewhere else.";
                                break;
                            case 523:
                                userErrorMessage = "Can't find a route.";
                                break;
                            case 524:
                                userErrorMessage =
                                    "There are no commands in the requested task. Add commands to the task.";
                                break;
                            default:
                                userErrorMessage =
                                    "Can't create a flight plan.";
                        }
                    }

                    let adminErrorMessage = "";
                    if (error?.response?.data?.message) {
                        adminErrorMessage = error.response.data.message;
                        console.error(adminErrorMessage);
                    } else {
                        adminErrorMessage = "Unexpected error";
                        console.error(error);
                    }
                    const errorMessage = isAdmin
                        ? `${userErrorMessage} (${adminErrorMessage})`
                        : userErrorMessage;
                    setPreviewAlert({
                        variant: "danger",
                        message: errorMessage,
                        droneId: taskPreview.droneId,
                    });

                    setTaskPreviewSuccess?.(false);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [
        organizationId,
        locationId,
        taskPreview,
        taskPreviewDroneId,
        taskPreviewUsesStartPosition,
        taskPreviewStartPosition,
        taskPreviewRoutePlannerVersion,
        isAdmin,
        setTaskPreviewSuccess,
    ]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Reload the route preview when map inputs that affect backend planning change.
    useEffect(() => {
        loadTaskPreview();
    }, [loadTaskPreview, flyzoneObjects, areas, docks]);

    const loadResumePreview = useCallback(() => {
        if (
            resumePreview?.missionId &&
            resumePreview?.droneId &&
            resumePreviewRoutePlannerVersion
        ) {
            setIsLoading(true);
            api.getMissionResumePreview(
                organizationId,
                locationId,
                resumePreview.missionId,
                resumePreview.droneId,
                resumePreviewRoutePlannerVersion,
            )
                .then((response) => {
                    if (resumePreviewRoutePlannerVersion === "V2") {
                        response.data = response.data.data;
                    }
                    // It could be that response.data.data is a string "mission completed". We ignore that for now. Could be handled later
                    if (Array.isArray(response.data)) {
                        const transformedCommands =
                            replaceCommandsSettingsWithArguments(response.data);
                        setResumePreviewCommands(transformedCommands);
                    }
                    setPreviewAlert(null);
                })
                .catch((error) => {
                    setResumePreviewCommands([]);
                    let userErrorMessage = "";
                    if (error?.response?.status) {
                        switch (error.response.status) {
                            case 520:
                                userErrorMessage =
                                    "There is no app in the requested task. Add an app to the task.";
                                break;
                            case 521:
                                userErrorMessage =
                                    "Drone is not in a location where it is allowed to fly. Place the drone in a flyzone.";
                                break;
                            case 522:
                                userErrorMessage =
                                    "Drone has not enough space to take off. Place the drone somewhere else.";
                                break;
                            case 523:
                                userErrorMessage = "Can't find a route.";
                                break;
                            case 524:
                                userErrorMessage =
                                    "There are no commands in the requested task. Add commands to the task.";
                                break;
                            default:
                                userErrorMessage =
                                    "Can't create a flight plan.";
                        }
                    }
                    let adminErrorMessage = "";
                    if (error?.response?.data?.message) {
                        adminErrorMessage = error.response.data.message;
                        console.error(adminErrorMessage);
                    } else {
                        adminErrorMessage = "Unexpected error";
                        console.error(error);
                    }
                    const errorMessage = isAdmin
                        ? `${userErrorMessage} (${adminErrorMessage})`
                        : userErrorMessage;
                    setPreviewAlert({
                        variant: "danger",
                        message: errorMessage,
                        droneId: resumePreview.droneId,
                    });
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [
        organizationId,
        locationId,
        resumePreview,
        resumePreviewRoutePlannerVersion,
        isAdmin,
    ]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Reload the resume preview when map inputs that affect backend planning change.
    useEffect(() => {
        loadResumePreview();
    }, [loadResumePreview, flyzoneObjects, areas, docks]);

    const focus = useCallback(
        ({ x, y, width, height, margin, force = false }) => {
            if (
                !mapReady ||
                !viewportRef.current ||
                (disableAutoFocus && !force)
            )
                return false;

            const normalizedX = Number(x);
            const normalizedY = Number(y);
            if (!Number.isFinite(normalizedX) || !Number.isFinite(normalizedY))
                return false;

            const normalizedWidth = Number(width);
            const normalizedHeight = Number(height);
            const hasDimensions =
                width !== undefined &&
                width !== null &&
                height !== undefined &&
                height !== null &&
                Number.isFinite(normalizedWidth) &&
                Number.isFinite(normalizedHeight);
            const extent = hasDimensions
                ? {
                      x: normalizedX,
                      y: normalizedY,
                      width: normalizedWidth,
                      height: normalizedHeight,
                  }
                : { x: normalizedX, y: normalizedY };

            viewportRef.current.fitBounds(extent, {
                worldPadding: Number.isFinite(Number(margin))
                    ? Number(margin)
                    : 0,
                minWorldVisibleSpan: 10,
            });
            return true;
        },
        [mapReady, disableAutoFocus],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: Refocus after the viewport is recreated, even though the generation counter is not read in the effect body.
    useEffect(() => {
        if (
            mapReady &&
            viewportRef.current &&
            taskPreviewCommands &&
            taskPreviewCommands.length > 0 &&
            worldRect
        ) {
            focus({
                ...getCommandsArea(taskPreviewCommands),
                margin: 5,
                force: true,
            });
        }
    }, [
        taskPreviewCommands,
        worldRect,
        mapReady,
        mapReadiness.generation,
        focus,
    ]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Refocus after the viewport is recreated, even though the generation counter is not read in the effect body.
    useEffect(() => {
        if (
            mapReady &&
            viewportRef.current &&
            resumePreviewCommands &&
            resumePreviewCommands.length > 0 &&
            worldRect
        ) {
            focus({
                ...getCommandsArea(resumePreviewCommands),
                margin: 5,
                force: true,
            });
        }
    }, [
        resumePreviewCommands,
        worldRect,
        mapReady,
        mapReadiness.generation,
        focus,
    ]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Refocus after the viewport is recreated, even though the generation counter is not read in the effect body.
    useEffect(() => {
        if (
            mapReady &&
            viewportRef.current &&
            flight?.commands &&
            flight?.commands.length > 0 &&
            worldRect
        ) {
            focus({ ...getCommandsArea(flight?.commands), margin: 5 });
            // Don't refocus on flight commands update
            setDisableAutoFocus(true);
        }
    }, [flight?.commands, worldRect, mapReady, mapReadiness.generation, focus]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Refocus after the viewport is recreated, even though the generation counter is not read in the effect body.
    useEffect(() => {
        if (
            mapReady &&
            viewportRef.current &&
            measurements &&
            measurements.length > 0 &&
            worldRect
        ) {
            const measurementsArea = getMeasurementsArea(
                measurements,
                activeMeasurementPoseMode,
            );
            if (measurementsArea && focus({ ...measurementsArea, margin: 5 })) {
                // Disable auto focus such that we don't refocus when measurements are updated during uploading
                console.log("Disable auto focus");
                setDisableAutoFocus(true);
            }
        }
    }, [
        measurements,
        activeMeasurementPoseMode,
        worldRect,
        mapReady,
        mapReadiness.generation,
        focus,
    ]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Refocus after the viewport is recreated, even though the generation counter is not read in the effect body.
    useEffect(() => {
        if (mapReady && viewportRef.current && point && worldRect) {
            if (focus({ ...point, margin: 10 })) {
                if (lockFocusOnPoint) {
                    console.log("Disable auto focus");
                    // Deep-linked points should retain focus instead of being
                    // replaced by other asynchronously loaded map content.
                    setDisableAutoFocus(true);
                }
            }
        }
    }, [
        point,
        lockFocusOnPoint,
        worldRect,
        mapReady,
        mapReadiness.generation,
        focus,
    ]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Refocus after the viewport is recreated, even though the generation counter is not read in the effect body.
    useEffect(() => {
        docks.forEach((dock) => {
            if (
                mapReady &&
                viewportRef.current &&
                dock.id === highlightedDockId &&
                worldRect
            ) {
                if (
                    focus({ x: dock.position.x, y: dock.position.y, margin: 5 })
                ) {
                    console.log("Disable auto focus");
                    // Disable auto focus such that other components don't steal focus.
                    // Kind of relying on that highlightedDockId is known earlier than the rest. TODO: Improve
                    setDisableAutoFocus(true);
                }
            }
        });
    }, [
        docks,
        highlightedDockId,
        worldRect,
        mapReady,
        mapReadiness.generation,
        focus,
    ]);

    useEffect(() => {
        if (hoverImageInfo?.imageUrl) {
            setHoverImageError(false);
        }
    }, [hoverImageInfo?.imageUrl]);

    const getDroneById = (droneId) =>
        operationalDrones.find((drone) => drone?.droneId === droneId);
    const isDroneSleeping = (droneId) =>
        getDroneById(droneId)?.isSleeping ?? false;

    return (
        <>
            {previewAlert && (
                <Alert
                    variant={previewAlert.variant}
                    className="m-0 d-flex justify-content-between "
                >
                    <div className="d-flex align-items-center me-3">
                        <FaExclamationCircle
                            className={`mt-1 me-2 text-danger flex-shrink-0`}
                            style={{ marginTop: "-2px" }}
                        />
                        <div>{previewAlert.message}</div>
                    </div>
                    <div className="d-flex flex-column flex-shrink-0">
                        <Button
                            className="ms-2 mb-1 px-2 py-1"
                            onClick={() => {
                                // Load both previews, the one which has all its settings will actually be loaded
                                loadTaskPreview();
                                loadResumePreview();
                            }}
                        >
                            <small>
                                <FaRedo
                                    className="me-2"
                                    style={{ marginTop: "-2px" }}
                                />
                                Retry
                            </small>
                        </Button>
                        {previewAlert.droneId &&
                            isDroneSleeping(previewAlert.droneId) && (
                                <Button
                                    className="ms-2 px-2 py-1"
                                    disabled={hasSentWakeUp}
                                    onClick={() => {
                                        setHasSentWakeUp(true);
                                        api.sendWakeUp(previewAlert.droneId);
                                        setTimeout(() => {
                                            setHasSentWakeUp(false);
                                        }, 5000);
                                    }}
                                >
                                    <small>
                                        {hasSentWakeUp ? (
                                            <>
                                                <FaClock
                                                    className="me-2"
                                                    style={{
                                                        marginTop: "-2px",
                                                    }}
                                                />
                                                Waking up {previewAlert.droneId}
                                            </>
                                        ) : (
                                            <>
                                                <FaClock
                                                    className="me-2"
                                                    style={{
                                                        marginTop: "-2px",
                                                    }}
                                                />
                                                Wake up {previewAlert.droneId}
                                            </>
                                        )}
                                    </small>
                                </Button>
                            )}
                    </div>
                </Alert>
            )}
            <div
                ref={mapRef}
                style={{
                    position: "relative",
                    width: "100%", // Use up the full space. Define the available space outside the map
                    height: "100%",
                    borderRadius: "inherit", // Inherit borderRadius to look nice in Cards
                    overflow: "hidden", // Hide overflow to look nice in Cards
                }}
            >
                <CorvusMap
                    ref={viewportRef}
                    worldRect={worldRect}
                    onReady={handleMapReady}
                    onUnhandledClick={handleUnhandledClick}
                >
                    <AreasLayer
                        id="areas"
                        items={renderedAreaObjects}
                        highlights={areaHighlightStyles}
                    />
                    <AreasLayer
                        id="batches"
                        items={renderedBatchAreaObjects}
                        highlights={batchHighlightStyles}
                    />
                    <FlyzonesLayer
                        id="flyzones"
                        items={renderedFlyzoneObjects}
                        onItemHover={inspect ? handleFlyzoneHover : undefined}
                        onItemLeave={clearTextTooltip}
                    />
                    <MarkersLayer
                        id="markers"
                        items={renderedMarkerObjects}
                        detectedIds={isAdmin ? detectedMarkerIds : undefined}
                        onItemHover={inspect ? handleMarkerHover : undefined}
                        onItemLeave={clearTextTooltip}
                    />
                    <DocksLayer
                        id="docks"
                        items={dockObjects}
                        selectedIds={selectedDockIds}
                        onItemHover={handleDockHover}
                        onItemLeave={clearTextTooltip}
                    />

                    <MeasurementImageOverlays
                        id="measurement-mosaic"
                        projectedImages={projectedMeasurementImages}
                        organizationId={organizationId}
                        locationId={locationId}
                        selectedMeasurementId={selectedMosaicMeasurementId}
                        selectedIds={selectedMosaicMeasurementIds}
                        onItemClick={handleMosaicMeasurementClick}
                    />

                    {!showMosaic && (
                        <MeasurementsLayer
                            id="measurements"
                            items={measurementObjects}
                            onItemClick={handleMeasurementClick}
                            onItemHover={handleMeasurementHover}
                            onItemLeave={clearMeasurementPreview}
                        />
                    )}
                    <FlylinesLayer
                        id="task-preview-flylines"
                        items={taskPreviewFlylineObjects}
                        onItemHover={handleFlylineHover}
                        onItemLeave={clearTextTooltip}
                    />
                    <FlylinesLayer
                        id="resume-preview-flylines"
                        items={resumePreviewFlylineObjects}
                        onItemHover={handleFlylineHover}
                        onItemLeave={clearTextTooltip}
                    />
                    <FlylinesLayer
                        id="flight-path-flylines"
                        items={flightPathFlylineObjects}
                        onItemHover={handleFlylineHover}
                        onItemLeave={clearTextTooltip}
                    />
                    <FlylinesLayer
                        id="return-path-flylines"
                        items={returnPathFlylineObjects}
                        onItemHover={handleFlylineHover}
                        onItemLeave={clearTextTooltip}
                    />
                    <WarningsLayer
                        id="warnings"
                        items={warningObjects}
                        onItemHover={handleWarningHover}
                        onItemLeave={clearTextTooltip}
                    />
                    <DronesLayer id="drones" items={renderedDroneObjects} />
                    <PointsLayer id="points" items={pointObjects} />
                </CorvusMap>
                {(mosaicAvailable ||
                    inspectModeButton ||
                    measurementPoseModeButton) && (
                    <div
                        style={{
                            position: "absolute",
                            top: "10px",
                            right: "10px",
                            zIndex: 100,
                            display: "flex",
                            flexDirection: "column",
                            gap: "6px",
                        }}
                    >
                        {mosaicAvailable &&
                            (supportsHover ? (
                                <OverlayTrigger
                                    placement="left"
                                    overlay={
                                        <Tooltip id={measurementModeTooltipId}>
                                            {measurementPresentationAction}
                                        </Tooltip>
                                    }
                                >
                                    {measurementModeButton}
                                </OverlayTrigger>
                            ) : (
                                measurementModeButton
                            ))}
                        {measurementPoseModeButton &&
                            (supportsHover ? (
                                <OverlayTrigger
                                    placement="left"
                                    overlay={
                                        <Tooltip
                                            id={measurementPoseModeTooltipId}
                                        >
                                            {measurementPoseAvailability ===
                                                "mixed" && "Mixed poses. "}
                                            {measurementPoseModeAction}
                                        </Tooltip>
                                    }
                                >
                                    {measurementPoseModeButton}
                                </OverlayTrigger>
                            ) : (
                                measurementPoseModeButton
                            ))}
                        {inspectModeButton &&
                            (supportsHover ? (
                                <OverlayTrigger
                                    placement="left"
                                    overlay={
                                        <Tooltip id={inspectModeTooltipId}>
                                            {inspectModeAction}
                                        </Tooltip>
                                    }
                                >
                                    {inspectModeButton}
                                </OverlayTrigger>
                            ) : (
                                inspectModeButton
                            ))}
                    </div>
                )}
                {hoverTextInfo && (
                    <div
                        id="hover-tooltip"
                        style={{
                            textAlign: "center",
                            position: "absolute",
                            top: hoverTextInfo.y,
                            left: hoverTextInfo.x,
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                            color: "white",
                            padding: "5px",
                            borderRadius: "5px",
                            pointerEvents: "none",
                            transform: "translate(-50%, calc(-100% - 15px))", // Center horizontally and move above the cursor
                            whiteSpace: "nowrap", // Prevent text wrapping
                        }}
                    >
                        {typeof hoverTextInfo.text === "string"
                            ? hoverTextInfo.text.split(/\n/).map((line, i) => (
                                  // biome-ignore lint/suspicious/noArrayIndexKey: Hover text lines are positional, may contain duplicates, and have no stable identifier.
                                  <React.Fragment key={i}>
                                      {line}
                                      <br />
                                  </React.Fragment>
                              ))
                            : hoverTextInfo.text}
                    </div>
                )}
                {hoverImageInfo && (
                    <div
                        id="hover-image"
                        style={{
                            position: "absolute",
                            top: hoverImageInfo.y,
                            left:
                                hoverImageSize && frameDimensions
                                    ? clampPreviewX(
                                          hoverImageInfo.x,
                                          hoverImageSize.width,
                                          frameDimensions.width,
                                      )
                                    : hoverImageInfo.x,
                            pointerEvents: "none",
                            transform: `translate(-50%, ${
                                hoverImageInfo.y >
                                (frameDimensions?.height ?? 0) / 2
                                    ? `calc(-100% - ${PREVIEW_OFFSET}px)`
                                    : `${PREVIEW_OFFSET}px`
                            })`,
                        }}
                    >
                        {hoverImageInfo.imageUrl &&
                            (hoverImageError ? (
                                <div
                                    style={{
                                        width: "auto",
                                        height: "auto",
                                        padding: "5px",
                                        backgroundColor: "#f0f0f0",
                                        color: "#888",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderRadius: "5px",
                                        boxShadow:
                                            "0 4px 8px rgba(0, 0, 0, 0.5)",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {hoverImageInfo.imageUrl === "Deleted"
                                        ? "Image deleted"
                                        : "Image not available"}
                                </div>
                            ) : (
                                <>
                                    {!hoverImageSize && (
                                        <div
                                            style={{
                                                padding: "8px",
                                                backgroundColor: "#f0f0f0",
                                                color: "#888",
                                                borderRadius: "5px",
                                                boxShadow:
                                                    "0 4px 8px rgba(0, 0, 0, 0.5)",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            Loading image…
                                        </div>
                                    )}

                                    <img
                                        src={hoverImageInfo.imageUrl}
                                        alt="Overlay"
                                        onLoad={(event) => {
                                            const image = event.currentTarget;

                                            const size =
                                                getMeasurementPreviewSize({
                                                    naturalWidth:
                                                        image.naturalWidth,
                                                    naturalHeight:
                                                        image.naturalHeight,
                                                    containerWidth:
                                                        frameDimensions?.width ??
                                                        window.innerWidth,
                                                    containerHeight:
                                                        frameDimensions?.height ??
                                                        window.innerHeight,
                                                });

                                            setHoverImageSize(size);
                                        }}
                                        style={{
                                            display: hoverImageSize
                                                ? "block"
                                                : "none",
                                            width: hoverImageSize?.width,
                                            height: hoverImageSize?.height,
                                            borderRadius: "5px",
                                            boxShadow:
                                                "0 4px 8px rgba(0, 0, 0, 0.5)",
                                            transform: `rotate(${hoverImageInfo?.rotation ?? 0}rad)`,
                                        }}
                                        onError={() => setHoverImageError(true)}
                                    />
                                </>
                            ))}
                    </div>
                )}
                {isLoading && (
                    <div
                        style={{
                            position: "absolute",
                            top: "10px",
                            left: "10px",
                            zIndex: 100, // Ensure it's above the map
                            pointerEvents: "none", // Ensure it does not interfere with interactions on the map
                        }}
                    >
                        <Spinner animation="border" variant="primary" />
                    </div>
                )}
            </div>
        </>
    );
}
